import { HttpError } from '../../config/Axios/axios-instance';
import * as actionTypes from './actionTypes';
import { EventActionTypes } from './actionTypes';
import { Event } from '../../domain/Event';
import { ListResults } from '../../common/List/List';

export interface EventStateType {
  event: Event | null;
  eventLoading: boolean;
  eventError: HttpError | null;

  createdEventsList: ListResults<Event> | null;
  createdEventsListLoading: boolean;
  createdEventsListError: HttpError | null;
  createdEventsListUpdateNeeded: boolean;

  eventsBySectionIdList: ListResults<Event> | null;
  eventsBySectionIdListLoading: boolean;
  eventsBySectionIdListError: HttpError | null;
  eventsBySectionIdListUpdateNeeded: boolean;

  createdEvent: Event | null;
  createEventLoading: boolean;
  createEventSuccess: boolean;
  createEventError: HttpError | null;

  updatedEvent: Event | null;
  updateEventLoading: boolean;
  updateEventSuccess: boolean;
  updateEventError: HttpError | null;

  updatedEventTitle: Event | null;
  updateEventTitleLoading: boolean;
  updateEventTitleSuccess: boolean;
  updateEventTitleError: HttpError | null;

  deleteEventLoading: boolean;
  deleteEventSuccess: boolean;
  deleteEventError: HttpError | null;
}

export type EventActionType = EventStateType & {
  type: EventActionTypes;
  certificateId: number;
};

export const initialState: EventStateType = {
  createdEventsList: null,
  createdEventsListLoading: false,
  createdEventsListError: null,
  createdEventsListUpdateNeeded: false,

  eventsBySectionIdList: null,
  eventsBySectionIdListLoading: false,
  eventsBySectionIdListError: null,
  eventsBySectionIdListUpdateNeeded: false,

  createdEvent: null,
  createEventLoading: false,
  createEventSuccess: false,
  createEventError: null,

  event: null,
  eventLoading: false,
  eventError: null,

  updatedEvent: null,
  updateEventLoading: false,
  updateEventSuccess: false,
  updateEventError: null,

  updatedEventTitle: null,
  updateEventTitleLoading: false,
  updateEventTitleSuccess: false,
  updateEventTitleError: null,

  deleteEventLoading: false,
  deleteEventSuccess: false,
  deleteEventError: null,
};

const fetchEventStart = (state: EventStateType): EventStateType => ({
  ...state,
  event: null,
  eventLoading: true,
  eventError: null,
});

const fetchEventSuccess = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  event: action.event,
  eventLoading: false,
  eventError: null,
});

const fetchEventFail = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  event: null,
  eventLoading: false,
  eventError: action.eventError,
});

const fetchCreatedEventsStart = (state: EventStateType): EventStateType => ({
  ...state,
  createdEventsListLoading: true,
  createdEventsListError: null,
});

const fetchCreatedEventsSuccess = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  createdEventsList: action.createdEventsList,
  createdEventsListLoading: false,
  createdEventsListError: null,
  createdEventsListUpdateNeeded: false,
});

const fetchCreatedEventsFail = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  createdEventsList: null,
  createdEventsListLoading: false,
  createdEventsListError: action.createdEventsListError,
});

const fetchEventsBySectionIdStart = (
  state: EventStateType,
): EventStateType => ({
  ...state,
  eventsBySectionIdListLoading: true,
  eventsBySectionIdListError: null,
});

const fetchEventsBySectionIdSuccess = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  eventsBySectionIdList: action.eventsBySectionIdList,
  eventsBySectionIdListLoading: false,
  eventsBySectionIdListError: null,
  eventsBySectionIdListUpdateNeeded: false,
});

const fetchEventsBySectionIdFail = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  eventsBySectionIdList: null,
  eventsBySectionIdListLoading: false,
  eventsBySectionIdListError: action.eventsBySectionIdListError,
});

const createEventStart = (state: EventStateType): EventStateType => ({
  ...state,
  createdEvent: null,
  createEventLoading: true,
  createEventError: null,
  createEventSuccess: false,
});

const createEventSuccess = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  createdEvent: action.createdEvent,
  createEventLoading: false,
  createEventError: null,
  createEventSuccess: true,
  createdEventsListUpdateNeeded: true,
});

const createEventFail = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  createdEvent: null,
  createEventLoading: false,
  createEventError: action.createEventError,
  createEventSuccess: false,
});

const resetCreateEventStore = (state: EventStateType): EventStateType => ({
  ...state,
  createdEvent: null,
  createEventLoading: false,
  createEventError: null,
  createEventSuccess: false,
});

const updateEventStart = (state: EventStateType): EventStateType => ({
  ...state,
  updatedEvent: null,
  updateEventLoading: true,
  updateEventError: null,
  updateEventSuccess: false,
});

const updateEventSuccess = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  updatedEvent: action.updatedEvent,
  updateEventLoading: false,
  updateEventError: null,
  updateEventSuccess: true,
  createdEventsListUpdateNeeded: true,
});

const updateEventFail = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  updatedEvent: null,
  updateEventLoading: false,
  updateEventError: action.updateEventError,
  updateEventSuccess: false,
});

const resetUpdateEventStore = (state: EventStateType): EventStateType => ({
  ...state,
  updatedEvent: null,
  updateEventLoading: false,
  updateEventSuccess: false,
  updateEventError: null,
});

const updateEventTitleStart = (state: EventStateType): EventStateType => ({
  ...state,
  updatedEventTitle: null,
  updateEventTitleLoading: true,
  updateEventTitleError: null,
  updateEventTitleSuccess: false,
});

const updateEventTitleSuccess = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  updatedEventTitle: action.updatedEventTitle,
  updateEventTitleLoading: false,
  updateEventTitleError: null,
  updateEventTitleSuccess: true,
  createdEventsListUpdateNeeded: true,
});

const updateEventTitleFail = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  updatedEventTitle: null,
  updateEventTitleLoading: false,
  updateEventTitleError: action.updateEventTitleError,
  updateEventTitleSuccess: false,
});

const resetUpdateEventTitleStore = (state: EventStateType): EventStateType => ({
  ...state,
  updatedEventTitle: null,
  updateEventTitleLoading: false,
  updateEventTitleError: null,
  updateEventTitleSuccess: false,
});

const deleteEventStart = (state: EventStateType): EventStateType => ({
  ...state,
  deleteEventLoading: true,
  deleteEventError: null,
  deleteEventSuccess: false,
});

const deleteEventSuccess = (state: EventStateType): EventStateType => ({
  ...state,
  deleteEventLoading: false,
  deleteEventError: null,
  deleteEventSuccess: true,
  createdEventsListUpdateNeeded: true,
});

const deleteEventFail = (
  state: EventStateType,
  action: EventActionType,
): EventStateType => ({
  ...state,
  deleteEventLoading: false,
  deleteEventError: action.deleteEventError,
  deleteEventSuccess: false,
});

const resetEventStore = (): EventStateType => ({
  ...initialState,
});

const reducer = (state = initialState, action: EventActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_EVENT_START:
      return fetchEventStart(state);
    case actionTypes.FETCH_EVENT_SUCCESS:
      return fetchEventSuccess(state, action);
    case actionTypes.FETCH_EVENT_FAIL:
      return fetchEventFail(state, action);
    case actionTypes.FETCH_CREATED_EVENTS_START:
      return fetchCreatedEventsStart(state);
    case actionTypes.FETCH_CREATED_EVENTS_SUCCESS:
      return fetchCreatedEventsSuccess(state, action);
    case actionTypes.FETCH_CREATED_EVENTS_FAIL:
      return fetchCreatedEventsFail(state, action);
    case actionTypes.FETCH_EVENTS_BY_SECTION_ID_START:
      return fetchEventsBySectionIdStart(state);
    case actionTypes.FETCH_EVENTS_BY_SECTION_ID_SUCCESS:
      return fetchEventsBySectionIdSuccess(state, action);
    case actionTypes.FETCH_EVENTS_BY_SECTION_ID_FAIL:
      return fetchEventsBySectionIdFail(state, action);
    case actionTypes.CREATE_EVENT_START:
      return createEventStart(state);
    case actionTypes.CREATE_EVENT_SUCCESS:
      return createEventSuccess(state, action);
    case actionTypes.CREATE_EVENT_FAIL:
      return createEventFail(state, action);
    case actionTypes.RESET_CREATE_EVENT_STORE:
      return resetCreateEventStore(state);
    case actionTypes.UPDATE_EVENT_START:
      return updateEventStart(state);
    case actionTypes.UPDATE_EVENT_SUCCESS:
      return updateEventSuccess(state, action);
    case actionTypes.UPDATE_EVENT_FAIL:
      return updateEventFail(state, action);
    case actionTypes.RESET_UPDATE_EVENT_STORE:
      return resetUpdateEventStore(state);
    case actionTypes.UPDATE_EVENT_TITLE_START:
      return updateEventTitleStart(state);
    case actionTypes.UPDATE_EVENT_TITLE_SUCCESS:
      return updateEventTitleSuccess(state, action);
    case actionTypes.UPDATE_EVENT_TITLE_FAIL:
      return updateEventTitleFail(state, action);
    case actionTypes.RESET_UPDATE_EVENT_TITLE_STORE:
      return resetUpdateEventTitleStore(state);
    case actionTypes.DELETE_EVENT_START:
      return deleteEventStart(state);
    case actionTypes.DELETE_EVENT_SUCCESS:
      return deleteEventSuccess(state);
    case actionTypes.DELETE_EVENT_FAIL:
      return deleteEventFail(state, action);
    case actionTypes.RESET_EVENT_STORE:
      return resetEventStore();
    default:
      return state;
  }
};

export default reducer;
